import env from "../variables/config";

const apiUrl = env.apiUrl;

function authHeaders(json = false) {
    const h: Record<string, string> = {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
    };
    if (json) h["Content-Type"] = "application/json";
    return h;
}

export type SuggestionSource = "enseigne" | "legal_name" | "sigle" | "sibling" | "cleaned";

export interface NameSuggestion {
    value: string;
    source: SuggestionSource;
}

export interface WeirdNameRuleStat {
    id: string;
    label: string;
    description: string;
    pattern: string;
    count: number;
}

export interface WeirdNameRulesResponse {
    total: number;
    rules: WeirdNameRuleStat[];
    computedAt: number;
    /** Counts are a full scan, so they are served from a 30-min cache unless refreshed. */
    cached: boolean;
}

export interface CompanySibling {
    id: string;
    name: string;
    siret: string;
    city: string;
    isWeird: boolean;
}

export interface WeirdNameCompany {
    siren: string;
    /** Legal name of the head office, or the first correctly-named establishment. */
    label: string;
    total: number;
    weirdCount: number;
    siblings: CompanySibling[];
}

export interface WeirdNameItem {
    id: string;
    name: string;
    legalName: string;
    type: string;
    brandGroup: string;
    franchise: { id: string; name: string } | null;
    city: string;
    postcode: string;
    siret: string;
    siren: string;
    enseigne: string;
    sigle: string;
    legalForm: string;
    status: string;
    isSiege: boolean;
    createdAt: string | null;
    matchedRules: string[];
    suggestions: NameSuggestion[];
    company: WeirdNameCompany | null;
}

export interface WeirdNameListResponse {
    items: WeirdNameItem[];
    total: number;
    page: number;
    totalPages: number;
    appliedRules: string[];
    mode: "rules" | "custom" | "company";
}

export interface SelectAllResponse {
    items: WeirdNameItem[];
    total: number;
    /** True when the cap cut the selection short — `total` says by how much. */
    truncated: boolean;
    cap: number;
}

export interface RenameResult {
    modifiedCount: number;
    /** Events whose companyName was realigned (matched on the establishment's SIRET). */
    eventsModified: number;
    changes: { id: string; siret: string; from: string; to: string }[];
    rejected: { id: string; reason: string }[];
}

/** One save request never carries more than this — larger batches are chunked. */
export const RENAME_CHUNK = 200;

async function handle(res: Response) {
    if (!res.ok) {
        let message = `HTTP ${res.status}`;
        try {
            message = (await res.json())?.error || message;
        } catch (_) {}
        throw new Error(message);
    }
    return res.json();
}

export async function fetchWeirdNameRules(refresh = false): Promise<WeirdNameRulesResponse> {
    const res = await fetch(`${apiUrl}/admin/weird-names/rules${refresh ? "?refresh=1" : ""}`, {
        headers: authHeaders(),
    });
    return handle(res);
}

export interface WeirdNameFilters {
    rules?: string[];
    q?: string;
    /** Free pattern: matches any name containing it, rules ignored. */
    custom?: string;
    /** Focus on one company: every establishment of that SIREN, well-named ones included. */
    siren?: string;
    sort?: "company" | "name" | "recent";
}

function toQuery(filters: WeirdNameFilters): URLSearchParams {
    const query = new URLSearchParams();
    if (filters.rules?.length) query.set("rules", filters.rules.join(","));
    if (filters.q) query.set("q", filters.q);
    if (filters.custom) query.set("custom", filters.custom);
    if (filters.siren) query.set("siren", filters.siren);
    if (filters.sort) query.set("sort", filters.sort);
    return query;
}

export async function fetchWeirdNames(
    params: WeirdNameFilters & { page?: number; limit?: number }
): Promise<WeirdNameListResponse> {
    const query = toQuery(params);
    if (params.page) query.set("page", String(params.page));
    if (params.limit) query.set("limit", String(params.limit));

    const res = await fetch(`${apiUrl}/admin/weird-names?${query.toString()}`, { headers: authHeaders() });
    return handle(res);
}

/** Every row matching the current filters, all pages at once (capped server-side). */
export async function fetchAllWeirdNames(filters: WeirdNameFilters): Promise<SelectAllResponse> {
    const res = await fetch(`${apiUrl}/admin/weird-names/select-all?${toQuery(filters).toString()}`, {
        headers: authHeaders(),
    });
    return handle(res);
}

export async function renameEstablishments(
    renames: { id: string; name: string }[],
    renameEvents = true
): Promise<RenameResult> {
    const res = await fetch(`${apiUrl}/admin/weird-names/rename`, {
        method: "PATCH",
        headers: authHeaders(true),
        body: JSON.stringify({ renames, renameEvents }),
    });
    return handle(res);
}

export const SUGGESTION_LABELS: Record<SuggestionSource, string> = {
    enseigne: "Enseigne",
    legal_name: "Raison sociale",
    sigle: "Sigle",
    sibling: "Autre établissement",
    cleaned: "Nom nettoyé",
};

export const pappersCompanyUrl = (siren: string) => `https://www.pappers.fr/entreprise/${siren}`;
export const pappersEstablishmentUrl = (siret: string) => `https://www.pappers.fr/recherche?q=${siret}`;
